import { useAuth0 } from "@auth0/auth0-react";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logoWhite.png";
import Api from "../../helper/ApiHandler";
import { COLORS } from "../../helper/colors";
import { SUB_MENU_LIST } from "../../helper/constants";
import { CustomToast } from "../../helper/Toast";
import { HelpIcon, HomeIcon, Loader, MoreIcon, ProfileIcon } from "../../icons";
import { setUserData } from "../../redux/users/reducer";
import { Drawer } from "../ui";

export const Layout = ({ from = null, loading = false, children }) => {
  const api = new Api();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { logout, isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const { pathname } = location;
  const ref = useRef(null);
  const [viewMenu, setViewMenu] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isChangePasswordLoading, setIsChangePasswordLoading] = useState(false);

  useEffect(() => {
    document.addEventListener("click", handleClickOutsideMenu, true);
  }, []);

  const handleClickOutsideMenu = (e) => {
    if (!ref.current.contains(e.target)) {
      setViewMenu((prev) => !prev);
    }
  };

  const logoutWithRedirect = async () => {
    await dispatch(setUserData(null));
    logout({
      returnTo: process.env.REACT_APP_AUTH0_LOGOUT_URL,
    });
  };

  const handleChangePassword = async () => {
    try {
      setViewMenu(true);
      setIsChangePasswordLoading(true);
      const token = await getAccessTokenSilently();
      const { message, status } = await api.post(
        "user/reset-password",
        { email: user?.email },
        token
      );
      if (status) {
        setIsChangePasswordLoading(false);
        setViewMenu(false);
        CustomToast("success", message);
      }
    } catch (err) {
      setIsChangePasswordLoading(false);
      setViewMenu(false);
      CustomToast("error", err.message);
    }
  };

  return (
    <div className="flex flex-col flex-1 h-screen">
      <div className="flex items-center p-8 flex-row justify-between bg-primary">
        <div
          className="flex items-center"
          role="button"
          onClick={() => {
            if (!!isAuthenticated) {
              navigate("/home");
            } else {
              navigate("/");
            }
          }}
        >
          <img src={Logo} className="h-10 w-40" alt="logo" />
        </div>
        {!!isAuthenticated && (
          <>
            <div className="sm:flex flex-1 justify-end items-center hidden">
              <HomeIcon
                height={32}
                width={32}
                fill={pathname === "/home" ? COLORS.textGray : COLORS.white}
                className="cursor-pointer  mr-4"
                onClick={() => {
                  navigate("/home");
                }}
              />
              <HelpIcon
                height={32}
                width={32}
                stroke={pathname === "/help" ? COLORS.textGray : COLORS.white}
                className="cursor-pointer  mr-4"
                onClick={() => {
                  navigate("/help");
                }}
              />
              <div className="flex justify-center items-center relative group">
                <ProfileIcon
                  height={32}
                  width={32}
                  fill={pathname === "/profile" && COLORS.textGray}
                  className="cursor-pointer"
                  onClick={() => setViewMenu((prev) => !prev)}
                />
                {!!viewMenu && (
                  <div ref={ref} className="absolute top-12 right-1">
                    <div className="flex flex-col w-48 rounded-lg shadow-2xl p-2 bg-white">
                      {SUB_MENU_LIST &&
                        SUB_MENU_LIST.map((list) => (
                          <div
                            className="flex w-full hover:bg-orange-200 rounded-md p-2 items-center justify-center cursor-pointer group"
                            onClick={async () => {
                              setViewMenu((prev) => !prev);
                              if (list?.name === "Logout") {
                                logoutWithRedirect();
                              } else if (list?.name === "Change Password") {
                                await handleChangePassword();
                              } else {
                                navigate(list?.navigateTo);
                              }
                            }}
                          >
                            <div className="w-8">{list?.icon}</div>
                            <div className="flex flex-1 items-center text-textGray">
                              {list.name !== "Change Password" ? (
                                list?.name
                              ) : !isChangePasswordLoading ? (
                                list?.name
                              ) : (
                                <Loader height={16} width={16} fill="#fff" />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div
              className="flex justify-end items-center ml-4 sm:hidden"
              onClick={() => setIsOpen(true)}
            >
              <MoreIcon height={32} width={32} fill={COLORS.white} />
            </div>
          </>
        )}

        <Drawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          pathname={pathname}
          logoutWithRedirect={logoutWithRedirect}
          handleChangePassword={handleChangePassword}
          isChangePasswordLoading={isChangePasswordLoading}
        />
      </div>

      <div
        className={clsx(
          "h-full max-h-full overflow-auto",
          "design-scrollbar",
          !from && "flex flex-col",
          !!from && "overflow-x-hidden"
        )}
      >
        <div className={clsx("", !from && "flex flex-1")}>{children}</div>
        {!loading && (
          <div className="text-center px-2 w-full py-4 items-center justify-center bg-primary text-white font-medium text-xs sm:text-xl">
            ©2022 - HUSK | All rights reserved
            {pathname !== "/privacy-policy" && (
              <>
                {" |"}
                <Link
                  className="underline ml-1.5"
                  to={"/privacy-policy"}
                  target="_blank"
                >
                  Privacy Policy
                </Link>
              </>
            )}
            {pathname !== "/terms-and-conditions" && (
              <>
                {" |"}
                <Link
                  className="underline ml-1.5"
                  to={"/terms-and-conditions"}
                  target="_blank"
                >
                  Terms and Conditions
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
